import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports } from '@/api/documents';
import { reconciliationActs, type SnapshotRow, type TheirRow } from '@/api/settlements';
import PrintActions from '@/shared/components/PrintActions';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { contractLabel } from '@/shared/utils/labels';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DOC_LABELS: Record<string, string> = {
  'doc.goods_receipts': 'Надходження',
  'doc.goods_sales': 'Реалізація',
  'doc.bank_receipts': 'Платіж (надход.)',
  'doc.bank_payments': 'Платіж (виплата)',
  'doc.cash_receipts': 'ПКО',
  'doc.cash_payments': 'РКО',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  'doc.debt_adjustments': 'Коригування боргу',
  'doc.additional_expenses': 'Додаткові витрати',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function FragmentHead({ td }: { td: React.CSSProperties }) {
  return (
    <>
      <th style={{ ...td, width: '9%' }}>Дата</th>
      <th style={{ ...td, width: '22%' }}>Документ</th>
      <th style={{ ...td, width: '9.5%' }}>Дебет</th>
      <th style={{ ...td, width: '9.5%' }}>Кредит</th>
    </>
  );
}

export default function ReconciliationActPrintPage() {
  const [params] = useSearchParams();
  const orgId = params.get('organization_id') ?? '';
  const contractorId = params.get('contractor_id') ?? '';
  const contractId = params.get('contract_id') ?? '';
  const dateFrom = params.get('date_from') ?? '';
  const dateTo = params.get('date_to') ?? '';
  // 0437: друк ІЗ ДОКУМЕНТА (act_id) — беремо ЙОГО snapshot і дані
  // контрагента (двоблокова форма 1С), а не перегенерацію з реєстру.
  const actId = params.get('act_id') ?? '';

  const actDocQ = useQuery({
    queryKey: ['reconciliation-act', actId],
    queryFn: () => reconciliationActs.get(actId),
    enabled: !!actId,
  });

  const act = useQuery({
    queryKey: ['reconciliation-print', orgId, contractorId, contractId, dateFrom, dateTo],
    queryFn: () => reports.reconciliationAct({
      organization_id: orgId,
      contractor_id: contractorId,
      contract_id: contractId || undefined,
      date_from: `${dateFrom}T00:00:00+02:00`,
      date_to: `${dateTo}T23:59:59+02:00`,
    }),
    enabled: !!orgId && !!contractorId && !actId,
  });

  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', orgId, contractorId],
    queryFn: () => refs.contracts({ organization_id: orgId, contractor_id: contractorId }),
    enabled: !!orgId && !!contractorId,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (act.isError || actDocQ.isError) {
    return (
      <div style={{ padding: '2rem', color: 'var(--badge-red-fg)' }}>
        Не вдалося завантажити дані акта. Поверніться назад і спробуйте ще раз
        (можливо, сесія завершилась — повторіть вхід).
      </div>
    );
  }
  const doc = actDocQ.data;
  // Уніфікована модель для рендера: з документа (act_id) або зі звіту.
  const view = doc ? (() => {
    const snap = ((doc.movements_snapshot ?? []) as SnapshotRow[]).map((r) => (
      // Старий snapshot (до 0437) ніс direction/amount без debit/credit.
      r.debit != null || r.credit != null ? r : {
        ...r,
        debit: r.direction === '+' ? r.amount : null,
        credit: r.direction === '-' ? r.amount : null,
      }));
    let bal = Number(doc.opening_balance_our);
    const rows = snap.map((r) => {
      bal += r.debit ? Number(r.debit) : -Number(r.credit ?? 0);
      return {
        document_id: r.doc_id, movement_date: r.date,
        document_type: r.doc_type, document_number: r.doc_number ?? '',
        debit: r.debit ?? '0', credit: r.credit ?? '0', balance: String(bal),
      };
    });
    return {
      opening_balance: doc.opening_balance_our,
      closing_balance: doc.closing_balance_our,
      turnover_debit: String(rows.reduce((t, r) => t + Number(r.debit), 0)),
      turnover_credit: String(rows.reduce((t, r) => t + Number(r.credit), 0)),
      rows,
    };
  })() : act.data;
  if (!view) {
    return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  }
  const theirRows = (doc?.their_movements ?? []) as TheirRow[];
  const theirOpeningRaw = doc?.opening_balance_their != null
    ? -Number(doc.opening_balance_their) : null;
  const theirClosingRaw = doc?.closing_balance_their != null
    ? -Number(doc.closing_balance_their) : null;

  const org = orgsQ.data?.items.find((o) => o.id === orgId);
  const ctr = contractorsQ.data?.items.find((c) => c.id === contractorId);
  const contract = contractsQ.data?.find((c) => c.id === contractId);

  const balance = Number(view.closing_balance);
  // Формулювання як у 1С (рішення власника 2026-08-19): «За даними <орг>
  // на <дата> заборгованість на користь <кого> складає <сума> грн».
  const orgFull = org?.full_name ?? org?.name ?? '—';
  const ctrFull = ctr?.full_name ?? ctr?.name ?? '—';
  const balanceDate = formatDate(doc?.period_end ?? dateTo);
  const balanceText = balance === 0
    ? `За даними ${orgFull} станом на ${balanceDate} заборгованість відсутня.`
    : `За даними ${orgFull} станом на ${balanceDate} заборгованість на користь `
      + `${balance > 0 ? orgFull : ctrFull} складає ${fmt(Math.abs(balance))} грн.`;

  return (
    <div className="print-page" style={{
      padding: '20mm', fontFamily: 'Times, serif', fontSize: '11pt', lineHeight: 1.4,
      maxWidth: '210mm', margin: '0 auto', background: 'var(--bg-panel)',
    }}>

      {/* Уніфіковані дії друкованої форми (як у видатковій/акті реалізації):
          друкувати + назад + «підписати і зберегти» (знімок з печаткою/
          підписом → вкладення документа → журнал ЕДО). docMeta лише при
          друці З ДОКУМЕНТА (act_id). */}
      <PrintActions docMeta={doc ? {
        organizationId: orgId,
        documentType: 'doc.reconciliation_acts',
        documentId: doc.id,
        filename: `Акт звірки ${doc.document_number}`,
        orgName: org?.name ?? null,
      } : null} />

      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '14pt' }}>АКТ ЗВІРКИ ВЗАЄМОРОЗРАХУНКІВ</h2>
        <div style={{ marginTop: '0.5rem' }}>
          станом на {formatDate(dateTo)}
        </div>
        <div style={{ fontSize: '10pt', color: '#555' }}>
          за період з {formatDate(dateFrom)} по {formatDate(dateTo)}
        </div>
      </div>

      <p style={{ marginBottom: '0.5rem' }}>
        Цей акт складено в тому, що стан взаєморозрахунків між:
      </p>
      <table style={{ width: '100%', marginBottom: '1rem', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top', padding: '0.3rem 0.5rem' }}>
              <strong>{org?.full_name ?? org?.name ?? '—'}</strong>
              {partyCodesText(org) && <div style={{ fontSize: '10pt' }}>{partyCodesText(org)}</div>}
            </td>
            <td style={{ width: '50%', verticalAlign: 'top', padding: '0.3rem 0.5rem' }}>
              <strong>{ctr?.full_name ?? ctr?.name ?? '—'}</strong>
              {partyCodesText(ctr) && <div style={{ fontSize: '10pt' }}>{partyCodesText(ctr)}</div>}
            </td>
          </tr>
        </tbody>
      </table>

      {contract && (
        <p style={{ marginBottom: '0.5rem' }}>
          За договором: <strong>{contractLabel(contract)}</strong>
        </p>
      )}

      {/* ДВОКОЛОНКОВА форма як у 1С (рішення власника 2026-08-19):
          зліва «За даними організації», справа «За даними контрагента» —
          одна таблиця, правий бік порожній, якщо даних немає. */}
      {(() => {
        const our = view.rows;
        const oursD = our.reduce((t, r) => t + Number(r.debit), 0);
        const oursC = our.reduce((t, r) => t + Number(r.credit), 0);
        const thD = theirRows.reduce((t, r) => t + (r.debit ? Number(r.debit) : 0), 0);
        const thC = theirRows.reduce((t, r) => t + (r.credit ? Number(r.credit) : 0), 0);
        const opening = Number(view.opening_balance);
        const closing = Number(view.closing_balance);
        const n = Math.max(our.length, theirRows.length, 1);
        const td = { padding: '2px 4px', border: '1px solid #000',
          fontSize: '8.5pt', verticalAlign: 'top' as const };
        const num = { ...td, textAlign: 'right' as const,
          whiteSpace: 'nowrap' as const };
        // Сальдо у форматі 1С: додатнє (дебетове) — у колонку Дебет,
        // кредитове — у Кредит.
        const balCells = (v: number | null) => v == null
          ? <><td style={num} /><td style={num} /></>
          : <>
              <td style={num}>{v > 0 ? fmt(v) : ''}</td>
              <td style={num}>{v < 0 ? fmt(-v) : ''}</td>
            </>;
        return (
          <table style={{ width: '100%', borderCollapse: 'collapse',
            marginTop: '1rem', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th colSpan={4} style={{ ...td, background: '#f5f5f5',
                  textAlign: 'left' }}>
                  За даними {org?.full_name ?? org?.name ?? '—'}, грн
                </th>
                <th colSpan={4} style={{ ...td, background: '#f5f5f5',
                  textAlign: 'left' }}>
                  За даними {ctr?.full_name ?? ctr?.name ?? 'контрагента'}, грн
                </th>
              </tr>
              <tr>
                {[0, 1].map((k) => (
                  <FragmentHead key={k} td={td} />
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ fontWeight: 600 }}>
                <td colSpan={2} style={td}>Сальдо початкове</td>
                {balCells(opening)}
                <td colSpan={2} style={td}>Сальдо початкове</td>
                {balCells(theirOpeningRaw)}
              </tr>
              {Array.from({ length: n }, (_, i) => {
                const l = our[i];
                const r = theirRows[i];
                return (
                  <tr key={i}>
                    <td style={td}>{l ? formatDate(l.movement_date) : ''}</td>
                    <td style={td}>
                      {l ? <>
                        {DOC_LABELS[l.document_type] ?? DOC_TABLE_NAMES_UK[l.document_type] ?? l.document_type}
                        {l.document_number ? ` №${l.document_number}` : ''}
                      </> : ''}
                    </td>
                    <td style={num}>{l && Number(l.debit) > 0 ? fmt(l.debit) : ''}</td>
                    <td style={num}>{l && Number(l.credit) > 0 ? fmt(l.credit) : ''}</td>
                    <td style={td}>{r?.date ? formatDate(r.date) : ''}</td>
                    <td style={td}>{r?.description ?? ''}</td>
                    <td style={num}>{r?.debit ? fmt(r.debit) : ''}</td>
                    <td style={num}>{r?.credit ? fmt(r.credit) : ''}</td>
                  </tr>
                );
              })}
              <tr style={{ fontWeight: 600 }}>
                <td colSpan={2} style={td}>Обороти за період</td>
                <td style={num}>{fmt(oursD)}</td>
                <td style={num}>{fmt(oursC)}</td>
                <td colSpan={2} style={td}>Обороти за період</td>
                <td style={num}>{theirRows.length ? fmt(thD) : ''}</td>
                <td style={num}>{theirRows.length ? fmt(thC) : ''}</td>
              </tr>
              <tr style={{ fontWeight: 700 }}>
                <td colSpan={2} style={td}>Сальдо кінцеве</td>
                {balCells(closing)}
                <td colSpan={2} style={td}>Сальдо кінцеве</td>
                {balCells(theirClosingRaw)}
              </tr>
            </tbody>
          </table>
        );
      })()}

      {doc && theirClosingRaw != null && (
        <p style={{ marginTop: '0.5rem', fontSize: '10pt' }}>
          Розбіжність між даними сторін:{' '}
          <strong>
            {Number(doc.discrepancy_amount) === 0
              ? 'відсутня'
              : `${fmt(doc.discrepancy_amount)} грн`}
          </strong>
        </p>
      )}

      <p style={{ marginTop: '1rem', fontSize: '11pt' }}>
        <strong>{balanceText}</strong>
      </p>

      <table style={{ width: '100%', marginTop: '3rem', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top', padding: '0.3rem 0.5rem' }}>
              <div>Від {org?.name ?? '—'}:</div>
              <div style={{ marginTop: '2.5rem', borderTop: '1px solid #000', paddingTop: '0.2rem', textAlign: 'center' }}>
                __________________________
              </div>
              <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
              <div style={{ marginTop: '0.5rem' }}>М.П.</div>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top', padding: '0.3rem 0.5rem' }}>
              <div>Від {ctr?.name ?? '—'}:</div>
              <div style={{ marginTop: '2.5rem', borderTop: '1px solid #000', paddingTop: '0.2rem', textAlign: 'center' }}>
                __________________________
              </div>
              <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
              <div style={{ marginTop: '0.5rem' }}>М.П.</div>
            </td>
          </tr>
        </tbody>
      </table>

    </div>
  );
}
